<template>
    <div v-if="user.data" class="container meal_plan_page">
        <div v-if="plan">
            <div class="plan_header">
                <h1>{{ plan.name }}</h1>
                <div class="view_toggle">
                    <button type="button" class="toggle_btn" :class="{ active: view_mode === 'calendar' }" @click="view_mode = 'calendar'">{{ $t('meal_plans.view_calendar') }}</button>
                    <button type="button" class="toggle_btn" :class="{ active: view_mode === 'list' }" @click="view_mode = 'list'">{{ $t('meal_plans.view_list') }}</button>
                </div>
            </div>
            <p class="error_text" v-if="error">{{ error }}</p>

            <button type="button" class="primary_btn add_recipe_btn" @click="openEntryModal(null, null)">{{ $t('meal_plans.add_recipe') }}</button>

            <MealPlanWeekGrid v-if="view_mode === 'calendar'"
                :entries="plan.entries"
                v-on:add-entry="prefill => openEntryModal(prefill.date, prefill.slot)"
                v-on:remove-entry="removeEntry"
            />
            <MealPlanEntryList v-else
                :entries="plan.entries"
                v-on:remove-entry="removeEntry"
            />

            <!-- Keyed by updated_at so the (compute-on-read) totals re-fetch after entry changes. -->
            <div class="nutrition_section" v-if="plan.entries.length > 0">
                <MealPlanNutrition :key="'nutrition_' + plan.updated_at" :plan_id="plan.id" />
            </div>

            <div class="checklist_section">
                <button type="button" class="primary_btn" v-if="!show_checklist" :disabled="plan.entries.length < 1" @click="show_checklist = true">{{ $t('meal_plans.checklist.show') }}</button>
                <MealPlanChecklist v-if="show_checklist" :key="plan.updated_at" :plan_id="plan.id" />
            </div>

            <MealPlanEntryModal v-if="show_entry_modal"
                :recipes="recipes"
                :given_date="modal_date"
                :given_slot="modal_slot"
                v-on:save="addEntry"
                v-on:decline-action="show_entry_modal = false"
            />
        </div>
        <p v-else-if="error" class="error_text">{{ error }}</p>
    </div>
    <div v-else>
        <h1>Δεν έχεις συνδεθεί.</h1>
        <router-link :to="'/login?fwd=' + $route.fullPath" class="primary_btn add_recipe">Σύνδεση ή Εγγραφή</router-link>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import MealPlanWeekGrid from './extras/Meal_plan_week_grid.vue';
import MealPlanEntryList from './extras/Meal_plan_entry_list.vue';
import MealPlanEntryModal from './extras/Meal_plan_entry_modal.vue';
import MealPlanChecklist from './extras/Meal_plan_checklist.vue';
import MealPlanNutrition from './extras/Meal_plan_nutrition.vue';

export default {
    name: 'MealPlanInternal',
    components: {
        MealPlanWeekGrid,
        MealPlanEntryList,
        MealPlanEntryModal,
        MealPlanChecklist,
        MealPlanNutrition
    },
    data() {
        return {
            plan: null,
            recipes: [],
            view_mode: 'calendar',
            show_entry_modal: false,
            modal_date: null,
            modal_slot: null,
            show_checklist: false,
            error: null
        }
    },
    computed: {
        ...mapGetters({
            user: "user"
        })
    },
    watch: {
        "$store.getters.user.data": function () {
            if (this.user.data) {
                this.getPlan();
            }
        }
    },
    mounted() {
        if (this.user.data) {
            this.getPlan();
        }
    },
    methods: {
        getPlan() {
            fetch(process.env.VUE_APP_API + `/mealplans/` + this.$route.params.id, {
                headers: {
                    "TOKEN": JSON.stringify(this.user.data.token)
                }
            }).then(res => res.json())
              .then(res => {
                  if (res.error) {
                      this.error = res.error;
                  } else {
                      this.plan = res.plan;
                  }
              });
        },
        getRecipes() {
            fetch(process.env.VUE_APP_API + `/recipes/getall`)
                .then(res => res.json())
                .then(res => {
                    if (!res.error) {
                        this.recipes = res;
                    }
                });
        },
        openEntryModal(date, slot) {
            if (this.recipes.length < 1) {
                this.getRecipes();
            }
            this.modal_date = date;
            this.modal_slot = slot;
            this.show_entry_modal = true;
        },
        addEntry(entry) {
            this.show_entry_modal = false;
            this.error = null;
            fetch(process.env.VUE_APP_API + `/mealplans/` + this.plan.id + `/entries`, {
                method: "post",
                headers: {
                    "TOKEN": JSON.stringify(this.user.data.token),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ entry: entry })
            }).then(res => res.json())
              .then(res => {
                  if (res.error) {
                      this.error = typeof res.error === "string" ? res.error : JSON.stringify(res.error);
                  } else {
                      this.plan = res.plan;
                  }
              });
        },
        removeEntry(entry_id) {
            this.error = null;
            fetch(process.env.VUE_APP_API + `/mealplans/` + this.plan.id + `/entries/` + entry_id, {
                method: "delete",
                headers: {
                    "TOKEN": JSON.stringify(this.user.data.token)
                }
            }).then(res => res.json())
              .then(res => {
                  if (res.error) {
                      this.error = typeof res.error === "string" ? res.error : JSON.stringify(res.error);
                  } else {
                      this.plan = res.plan;
                  }
              });
        }
    }
}
</script>

<style scoped>
.meal_plan_page {
    text-align: left;
    padding-top: 2rem;
}
.plan_header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.5rem;
}
.view_toggle {
    display: inline-flex;
    border: 1px solid var(--color-grey-1);
    border-radius: 50px;
    overflow: hidden;
}
.toggle_btn {
    background: none;
    border: none;
    padding: 0.3rem 1rem;
    cursor: pointer;
    color: var(--color-grey-2);
}
.toggle_btn.active {
    background-color: var(--color-primary);
    color: white;
}
.add_recipe_btn {
    margin: 1rem 0;
}
.nutrition_section {
    margin-top: 2rem;
}
.checklist_section {
    margin-top: 2rem;
}
.error_text {
    color: var(--color-grey-2);
}
</style>
